import { useEffect, useState, useCallback } from 'react';
import { api } from '@shared/lib/api';

interface StripeTransfer {
  id: string;
  amount: number;
  created: number;
  description: string | null;
}

interface TransfersResponse {
  success: boolean;
  transfers: StripeTransfer[];
  has_more: boolean;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="transition-colors font-mono text-[10px]"
      title="Copy full ID"
      style={{ color: hovered ? 'var(--dash-text-tertiary)' : 'var(--dash-text-faint)' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {copied ? '\u2713' : '\u2398'}
    </button>
  );
}

function formatDateTime(unix: number) {
  return new Date(unix * 1000).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface Props {
  apiKey: string;
}

export default function StripeTransferHistory({ apiKey }: Props) {
  const [transfers, setTransfers] = useState<StripeTransfer[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [hoveredLoadMore, setHoveredLoadMore] = useState(false);

  const fetchTransfers = useCallback(async (startingAfter?: string) => {
    const params: Record<string, string> = {};
    if (startingAfter) params.starting_after = startingAfter;
    const data = await api.stripeTransfers(apiKey, params) as TransfersResponse;
    return data;
  }, [apiKey]);

  useEffect(() => {
    if (!apiKey) return;
    fetchTransfers()
      .then((data) => {
        setTransfers(data.transfers || []);
        setHasMore(data.has_more || false);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [apiKey, fetchTransfers]);

  const loadMore = async () => {
    if (transfers.length === 0) return;
    setLoadingMore(true);
    try {
      const lastId = transfers[transfers.length - 1].id;
      const data = await fetchTransfers(lastId);
      setTransfers((prev) => [...prev, ...(data.transfers || [])]);
      setHasMore(data.has_more || false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="mb-6">
      <div className="mb-3">
        <h2
          className="font-sans text-sm font-medium"
          style={{ color: 'var(--dash-text-secondary)' }}
        >
          Transfer History
        </h2>
        <p
          className="font-sans text-xs"
          style={{ color: 'var(--dash-text-dim)' }}
        >
          Funds transferred to your Stripe account after each payment
        </p>
      </div>

      {loading ? (
        <div
          className="rounded p-6"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <div className="animate-pulse space-y-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-4 rounded w-full"
                style={{ background: 'var(--dash-hover-bg)' }}
              />
            ))}
          </div>
        </div>
      ) : error ? (
        <div
          className="rounded p-6"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>{error}</p>
        </div>
      ) : transfers.length === 0 ? (
        <div
          className="rounded p-8 text-center"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <div className="text-2xl mb-3 opacity-20">&harr;</div>
          <p
            className="font-sans text-sm mb-1"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            No transfers yet
          </p>
          <p
            className="font-sans text-xs max-w-md mx-auto"
            style={{ color: 'var(--dash-text-faint)' }}
          >
            Transfers are created automatically after each successful payment.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded overflow-x-auto" style={{ border: '1px solid var(--dash-border)' }}>
            <table className="w-full text-sm font-sans">
              <thead>
                <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                  <th
                    className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Transfer ID
                  </th>
                  <th
                    className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Amount
                  </th>
                  <th
                    className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Date
                  </th>
                  <th
                    className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium"
                    style={{ color: 'var(--dash-text-dim)' }}
                  >
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((transfer, i) => (
                  <tr
                    key={transfer.id}
                    className="transition-colors last:border-b-0"
                    style={{
                      background: hoveredRow === transfer.id
                        ? 'var(--dash-hover-bg)'
                        : i % 2 === 0
                          ? 'var(--dash-row-even)'
                          : 'var(--dash-row-odd)',
                      borderBottom: '1px solid var(--dash-border-light)',
                    }}
                    onMouseEnter={() => setHoveredRow(transfer.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="font-mono text-xs"
                          style={{ color: 'var(--dash-text-tertiary)' }}
                        >
                          {transfer.id.slice(0, 11)}...
                        </span>
                        <CopyButton text={transfer.id} />
                      </div>
                    </td>
                    <td
                      className="px-4 py-2.5 font-mono text-xs"
                      style={{ color: 'var(--dash-text-primary)' }}
                    >
                      ${(transfer.amount / 100).toFixed(2)}
                    </td>
                    <td
                      className="px-4 py-2.5 font-mono text-xs"
                      style={{ color: 'var(--dash-text-dim)' }}
                    >
                      {formatDateTime(transfer.created)}
                    </td>
                    <td
                      className="px-4 py-2.5 font-sans text-xs"
                      style={{ color: 'var(--dash-text-muted)' }}
                    >
                      {transfer.description || '\u2014'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="w-full py-2 mt-2 font-mono text-xs rounded transition-colors disabled:opacity-40"
              style={{
                border: '1px solid var(--dash-border)',
                color: hoveredLoadMore ? 'var(--dash-text-secondary)' : 'var(--dash-text-muted)',
                background: hoveredLoadMore ? 'var(--dash-hover-bg)' : 'transparent',
              }}
              onMouseEnter={() => setHoveredLoadMore(true)}
              onMouseLeave={() => setHoveredLoadMore(false)}
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
